/**
 * Shop Customization Service
 * Handles shop visual customization (banners, themes, layouts)
 */

import { ShopBanner, ShopTheme, ShopExtended } from '../types/shop';
import { shopMutations, shopQueries } from '../../data/shops';
import { logger } from '../../infrastructure/logger';

export const shopCustomizationService = {
    /**
     * Get shop banners
     */
    async getShopBanners(shopId: string): Promise<ShopBanner[]> {
        try {
            return await shopQueries.getBanners(shopId);
        } catch (error) {
            logger.error('[ShopCustomizationService] Error getting banners', error);
            return [];
        }
    },

    /**
     * Add shop banner
     */
    async addShopBanner(
        shopId: string,
        banner: Omit<ShopBanner, 'id'>
    ): Promise<{ success: boolean; bannerId?: string; error?: string }> {
        try {
            const bannerId = await shopMutations.addBanner(shopId, banner);
            return { success: true, bannerId };
        } catch (error) {
            logger.error('[ShopCustomizationService] Error adding banner', error);
            return { success: false, error: "Error al agregar el banner" };
        }
    },

    /**
     * Update shop banner
     */
    async updateShopBanner(
        bannerId: string,
        updates: Partial<ShopBanner>
    ): Promise<{ success: boolean; error?: string }> {
        try {
            await shopMutations.updateBanner(bannerId, updates);
            return { success: true };
        } catch (error) {
            logger.error('[ShopCustomizationService] Error updating banner', error);
            return { success: false, error: "Error al actualizar el banner" };
        }
    },

    /**
     * Delete shop banner
     */
    async deleteShopBanner(bannerId: string): Promise<{ success: boolean; error?: string }> {
        try {
            await shopMutations.deleteBanner(bannerId);
            return { success: true };
        } catch (error) {
            logger.error('[ShopCustomizationService] Error deleting banner', error);
            return { success: false, error: "Error al eliminar el banner" };
        }
    },

    /**
     * Update shop theme
     */
    async updateShopTheme(shopId: string, theme: ShopTheme): Promise<{ success: boolean; error?: string }> {
        try {
            await shopMutations.updateShopTheme(shopId, theme);
            return { success: true };
        } catch (error) {
            logger.error('[ShopCustomizationService] Error updating theme', error);
            return { success: false, error: "Error al actualizar el tema de la tienda" };
        }
    },

    /**
     * Update shop layout
     */
    async updateShopLayout(
        shopId: string,
        layout: ShopExtended['layout']
    ): Promise<{ success: boolean; error?: string }> {
        try {
            await shopMutations.updateShopLayout(shopId, layout);
            return { success: true };
        } catch (error) {
            logger.error('[ShopCustomizationService] Error updating layout', error);
            return { success: false, error: "Error al actualizar el diseño de la tienda" };
        }
    }
};
